/**
 * Aura Theme - Wishlist
 * Stores product IDs in localStorage.
 * Storage key: "aura__wishlist-items"
 * Compatible with <button-wishlist data-product-id="..." data-action="add|remove">
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'aura__wishlist-items';

  /* ─── Helpers ─────────────────────────────────────────────────── */
  function getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function saveItems(arr) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  }

  function isInWishlist(id) {
    return getItems().includes(String(id));
  }

  function addItem(id) {
    const items = getItems();
    if (!items.includes(String(id))) {
      items.push(String(id));
      saveItems(items);
    }
  }

  function removeItem(id) {
    const items = getItems().filter((i) => i !== String(id));
    saveItems(items);
  }

  function getCount() {
    return getItems().length;
  }

  /* ─── Toast de confirmación ───────────────────────────────────── */
  let toastEl = null;
  let toastTimer = null;

  function showToast(message, isRemove) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.id = 'aura-wishlist-toast';
      toastEl.style.cssText = [
        'position:fixed',
        'bottom:24px',
        'left:50%',
        'transform:translateX(-50%) translateY(20px)',
        'background:#101010',
        'color:#fff',
        'font-family:\'DM Sans\',sans-serif',
        'font-size:14px',
        'font-weight:500',
        'padding:12px 24px',
        'border-radius:60px',
        'z-index:9999',
        'pointer-events:none',
        'opacity:0',
        'transition:opacity 0.3s ease, transform 0.3s ease',
        'white-space:nowrap',
        'box-shadow:0 4px 20px rgba(0,0,0,0.2)',
      ].join(';');
      document.body.appendChild(toastEl);
    }

    toastEl.textContent = message;
    // Si se añadió, mostrar corazón rojo en el toast
    if (!isRemove) {
      toastEl.style.background = '#DC4646';
    } else {
      toastEl.style.background = '#101010';
    }

    // Forzar reflow para reiniciar transición
    toastEl.style.opacity = '0';
    toastEl.style.transform = 'translateX(-50%) translateY(20px)';
    void toastEl.offsetWidth;

    toastEl.style.opacity = '1';
    toastEl.style.transform = 'translateX(-50%) translateY(0)';

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.style.opacity = '0';
      toastEl.style.transform = 'translateX(-50%) translateY(20px)';
    }, 2500);
  }

  /* ─── Update counters in header ───────────────────────────────── */
  function updateCounters() {
    const count = getCount();
    document.querySelectorAll('[data-aura-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ─── Sync all buttons for a product ─────────────────────────── */
  function syncAllButtons(productId) {
    document.querySelectorAll(`button-wishlist[data-product-id="${productId}"]`).forEach((btn) => {
      btn._syncState && btn._syncState();
    });
  }

  /* ─── Custom Element ──────────────────────────────────────────── */
  class ButtonWishlist extends HTMLElement {
    connectedCallback() {
      this._productId = this.dataset.productId;
      this._syncState();
      this.addEventListener('click', this._handleClick.bind(this));
    }

    _handleClick(e) {
      e.preventDefault();

      const inList = isInWishlist(this._productId);

      if (inList) {
        removeItem(this._productId);
        this._setState(false);
        showToast('❤️ Eliminado de tu lista', true);
        this.dispatchEvent(new CustomEvent('wishlist:removed', { bubbles: true, detail: { id: this._productId } }));
      } else {
        addItem(this._productId);
        this._setState(true);
        this._animatePop();
        showToast('❤️ Guardado en tu lista', false);
        this.dispatchEvent(new CustomEvent('wishlist:added', { bubbles: true, detail: { id: this._productId } }));
      }

      // Sync todos los botones de la página con el mismo producto
      syncAllButtons(this._productId);
      updateCounters();
    }

    _syncState() {
      this._setState(isInWishlist(this._productId));
    }

    _setState(active) {
      this.classList.toggle('is-active', active);
      this.setAttribute('aria-pressed', String(active));
      this.setAttribute(
        'aria-label',
        active ? (this.dataset.tooltipRemove || 'Eliminar de wishlist') : (this.dataset.tooltipAdd || 'Añadir a wishlist')
      );
    }

    _animatePop() {
      this.classList.remove('aura-wishlist-pop');
      void this.offsetWidth; // Force reflow
      this.classList.add('aura-wishlist-pop');
      this.addEventListener('animationend', () => this.classList.remove('aura-wishlist-pop'), { once: true });
    }
  }

  /* ─── Register ────────────────────────────────────────────────── */
  if (!customElements.get('button-wishlist')) {
    customElements.define('button-wishlist', ButtonWishlist);
  }

  /* ─── Init Wishlist Page & Live Listeners ─────────────────────── */
  function initWishlistPage() {
    const pageEl = document.querySelector('[data-section-type="page-wishlist"]');
    if (!pageEl) return;

    const innerEl = pageEl.querySelector('.tw-page-wishlist-inner');
    const mainEl = pageEl.querySelector('.tw-page-wishlist-main');
    const emptyEl = pageEl.querySelector('.tw-wishlist-empty');

    const items = getItems();

    if (items.length === 0) {
      if (emptyEl) emptyEl.classList.remove('tw-hidden');
      if (innerEl) innerEl.classList.remove('tw-hidden');
      const gridEl = pageEl.querySelector('.tw-wishlist-grid');
      if (gridEl) gridEl.remove();
      return;
    }

    const query = items.map(id => `id:${id}`).join(' OR ');
    const sectionId = pageEl.getAttribute('data-section-id');
    const ajaxUrl = `${window.Shopify?.routes?.root || '/'}search?type=product&q=${encodeURIComponent(query)}&section_id=${sectionId}`;

    fetch(ajaxUrl)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('.tw-wishlist-grid');

        const oldGrid = pageEl.querySelector('.tw-wishlist-grid');
        if (oldGrid) {
          oldGrid.replaceWith(newGrid || '');
        } else if (newGrid) {
          mainEl.insertBefore(newGrid, emptyEl);
        }

        if (newGrid && newGrid.children.length > 0) {
          if (emptyEl) emptyEl.classList.add('tw-hidden');
        } else {
          if (emptyEl) emptyEl.classList.remove('tw-hidden');
        }

        if (innerEl) innerEl.classList.remove('tw-hidden');

        // Sync all buttons on the loaded cards
        items.forEach(id => {
          syncAllButtons(id);
        });
      })
      .catch(err => {
        console.error('Error fetching wishlist products:', err);
        if (emptyEl) emptyEl.classList.remove('tw-hidden');
        if (innerEl) innerEl.classList.remove('tw-hidden');
      });
  }

  // Listener para remover dinámicamente el producto del grid cuando se elimina de favoritos
  document.addEventListener('wishlist:removed', (e) => {
    const pageEl = document.querySelector('[data-section-type="page-wishlist"]');
    if (!pageEl) return;

    const productId = String(e.detail.id);
    const btn = pageEl.querySelector(`button-wishlist[data-product-id="${productId}"]`);
    if (btn) {
      const itemEl = btn.closest('.tw-wishlist-item');
      if (itemEl) {
        itemEl.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        itemEl.style.opacity = '0';
        itemEl.style.transform = 'scale(0.9)';
        setTimeout(() => {
          itemEl.remove();
          const gridEl = pageEl.querySelector('.tw-wishlist-grid');
          if (gridEl && gridEl.children.length === 0) {
            const emptyEl = pageEl.querySelector('.tw-wishlist-empty');
            if (emptyEl) emptyEl.classList.remove('tw-hidden');
          }
        }, 300);
      }
    }
  });

  /* ─── Init counters and wishlist on load ─────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    updateCounters();
    initWishlistPage();
  });

})();
